import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { type OrganizationEvent } from '@apps/web/hooks/use_organization_events';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { toast } from '@apps/web/lib/app_notifications';
import { useMode } from '@apps/web/lib/mode_store';
import {
    batchUpdateRecommendationCaregiverSelections,
    type RecommendationUnitType,
    useOutreachCaregiverSelections,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OutreachCaregiverSelectionRead } from '@libraries/typescript/api_client';

export type OutreachV3SelectionState = {
    readonly isReady: boolean;
    readonly stagedCaregiverIds: ReadonlySet<string>;
    /** Staged shift ids per caregiver, across the whole outreach. */
    readonly selectionCoverageByCaregiverId: ReadonlyMap<
        string,
        ReadonlySet<string>
    >;
    readonly persistSelectionDelta: (delta: {
        readonly addedIds: ReadonlyArray<string>;
        readonly removedIds: ReadonlyArray<string>;
    }) => void;
};

const EMPTY_SELECTIONS: ReadonlyArray<OutreachCaregiverSelectionRead> = [];
const EMPTY_OVERRIDES: ReadonlyMap<string, boolean> = new Map();
const SELECTION_EVENT_THROTTLE_MS = 3000;

function selectionMatchesUnit(
    selection: OutreachCaregiverSelectionRead,
    unitType: RecommendationUnitType,
    unitId: string,
) {
    return unitType === 'shift_group'
        ? selection.group_id === unitId
        : selection.shift_id === unitId;
}

/**
 * The v3-born caregiver table's checkbox state for one recommendation unit.
 *
 * The server's selection rows are the truth and the checkbox is derived from
 * them, so a repaint driven by the runner or another coordinator lands on an
 * open page. `stagedOverrides` holds only the deltas a write has not been
 * confirmed for yet: each one is dropped the moment fresh rows agree with it,
 * so a slow response can never invert a newer server state, and a failed
 * write rolls back exactly the ids that failed.
 *
 * Unchecking dismisses a suggestion the agent staged (the coordinator is
 * overruling Phoebe, which the agent must see) but withdraws one a
 * coordinator staged. `locallyStagedIdsRef` carries that signal for a row
 * this session just staged, because the server rows still describe the
 * pre-stage world until the refetch lands.
 */
export function useOutreachV3SelectionState({
    organizationId,
    outreachId,
    unitType,
    unitId,
    enabled,
    onDismissalsPersisted,
}: {
    readonly organizationId: string | null;
    readonly outreachId: string | null | undefined;
    readonly unitType: RecommendationUnitType;
    readonly unitId: string | null;
    readonly enabled: boolean;
    readonly onDismissalsPersisted?: (
        caregiverIds: ReadonlyArray<string>,
    ) => void;
}): OutreachV3SelectionState {
    const mode = useMode();
    const { data, error, mutate } = useOutreachCaregiverSelections(
        organizationId,
        outreachId,
        { reason: 'inhouse-recommendations-table-v3-selections' },
        { enabled },
    );
    const [stagedOverrides, setStagedOverrides] =
        useState<ReadonlyMap<string, boolean>>(EMPTY_OVERRIDES);
    const locallyStagedIdsRef = useRef<Set<string>>(new Set());

    // Throttling belongs after the outreach-id filter: a listener-level
    // throttle coalesces every organization's events, so an event for
    // another outreach can swallow this one's trailing refetch.
    const throttledRefresh = useThrottledCallback(() => {
        void mutate();
    }, SELECTION_EVENT_THROTTLE_MS);
    useEffect(() => throttledRefresh.cancel, [outreachId, throttledRefresh]);
    const handleSelectionEvent = useCallback(
        (event: OrganizationEvent) => {
            if (
                event.resource.type === 'outreach' &&
                event.resource.id === outreachId
            ) {
                throttledRefresh();
            }
        },
        [outreachId, throttledRefresh],
    );
    // Selection rows change under two different signals and neither covers
    // the other: the REST selection routes take their own lock and publish
    // only the suggestion events, while a write through the outreach lock
    // (adding shifts propagates a group's dismissals onto them) publishes
    // only `outreach_updated`.
    useListenForOrganizationEvent(
        'outreach_suggestion_added',
        handleSelectionEvent,
        { organizationId },
    );
    useListenForOrganizationEvent(
        'outreach_suggestion_removed',
        handleSelectionEvent,
        { organizationId },
    );
    useListenForOrganizationEvent('outreach_updated', handleSelectionEvent, {
        organizationId,
    });

    const unitSelections = useMemo(() => {
        if (data === undefined || unitId === null) {
            return EMPTY_SELECTIONS;
        }
        return data.selections.filter((selection) =>
            selectionMatchesUnit(selection, unitType, unitId),
        );
    }, [data, unitId, unitType]);

    const serverStagedCaregiverIds = useMemo(
        () =>
            new Set(
                unitSelections
                    .filter((selection) => selection.status === 'staged')
                    .map((selection) => selection.caregiver_id),
            ),
        [unitSelections],
    );

    // Routing input, not display: unchecking a row the agent staged
    // dismisses it, while one a coordinator staged is withdrawn. A group
    // unit holds one row per shift, so the decision reduces with OR across
    // the caregiver's rows — a per-caregiver map would let whichever row
    // came last in iteration order decide for all of them.
    const userStagedCaregiverIds = useMemo(
        () =>
            new Set(
                unitSelections
                    .filter(
                        (selection) =>
                            selection.status === 'staged' &&
                            selection.status_changed_by === 'user',
                    )
                    .map((selection) => selection.caregiver_id),
            ),
        [unitSelections],
    );

    const scopeKey = `${mode}:${outreachId ?? ''}:${unitType}:${unitId ?? ''}`;
    useEffect(() => {
        locallyStagedIdsRef.current = new Set();
        setStagedOverrides((previousOverrides) =>
            previousOverrides.size === 0 ? previousOverrides : EMPTY_OVERRIDES,
        );
    }, [scopeKey]);

    // Reconcile against every arrival of fresh rows: an override survives
    // only while the server still disagrees with it, so a response that
    // predates the write cannot clear it.
    useEffect(() => {
        for (const caregiverId of [...locallyStagedIdsRef.current]) {
            if (userStagedCaregiverIds.has(caregiverId)) {
                locallyStagedIdsRef.current.delete(caregiverId);
            }
        }
        setStagedOverrides((previousOverrides) => {
            if (previousOverrides.size === 0) {
                return previousOverrides;
            }
            const nextOverrides = new Map(previousOverrides);
            for (const [caregiverId, isStaged] of previousOverrides) {
                if (serverStagedCaregiverIds.has(caregiverId) === isStaged) {
                    nextOverrides.delete(caregiverId);
                }
            }
            return nextOverrides.size === previousOverrides.size
                ? previousOverrides
                : nextOverrides;
        });
    }, [serverStagedCaregiverIds, userStagedCaregiverIds]);

    // Per-shift staged rows for the whole outreach, keyed by caregiver. The
    // caller intersects these with the group's `shift_ids` from the outreach
    // payload, so coverage never depends on `group_id`, which the server
    // nulls when a group is deleted.
    const selectionCoverageByCaregiverId = useMemo(() => {
        const coverage = new Map<string, Set<string>>();
        for (const selection of data?.selections ?? []) {
            if (selection.status !== 'staged') {
                continue;
            }
            const shiftIds = coverage.get(selection.caregiver_id);
            if (shiftIds) {
                shiftIds.add(selection.shift_id);
            } else {
                coverage.set(
                    selection.caregiver_id,
                    new Set([selection.shift_id]),
                );
            }
        }
        return coverage;
    }, [data]);

    const stagedCaregiverIds = useMemo(() => {
        if (stagedOverrides.size === 0) {
            return serverStagedCaregiverIds;
        }
        const ids = new Set(serverStagedCaregiverIds);
        for (const [caregiverId, isStaged] of stagedOverrides) {
            if (isStaged) {
                ids.add(caregiverId);
            } else {
                ids.delete(caregiverId);
            }
        }
        return ids;
    }, [serverStagedCaregiverIds, stagedOverrides]);

    const persistSelectionDelta = useCallback(
        ({
            addedIds,
            removedIds,
        }: {
            readonly addedIds: ReadonlyArray<string>;
            readonly removedIds: ReadonlyArray<string>;
        }) => {
            if (
                !organizationId ||
                !outreachId ||
                unitId === null ||
                (addedIds.length === 0 && removedIds.length === 0)
            ) {
                return;
            }
            setStagedOverrides((previousOverrides) => {
                const nextOverrides = new Map(previousOverrides);
                for (const caregiverId of addedIds) {
                    nextOverrides.set(caregiverId, true);
                }
                for (const caregiverId of removedIds) {
                    nextOverrides.set(caregiverId, false);
                }
                return nextOverrides;
            });

            const writes = [
                ...addedIds.map((caregiverId) => {
                    locallyStagedIdsRef.current.add(caregiverId);
                    return {
                        caregiverId,
                        staged: true,
                        wasLocallyStaged: false,
                        action: 'stage' as const,
                    };
                }),
                ...removedIds.map((caregiverId) => {
                    const wasLocallyStaged =
                        locallyStagedIdsRef.current.has(caregiverId);
                    const stagedByCoordinator =
                        wasLocallyStaged ||
                        userStagedCaregiverIds.has(caregiverId);
                    locallyStagedIdsRef.current.delete(caregiverId);
                    return {
                        caregiverId,
                        staged: false,
                        wasLocallyStaged,
                        action: stagedByCoordinator
                            ? ('withdraw' as const)
                            : ('dismiss' as const),
                    };
                }),
            ];

            void batchUpdateRecommendationCaregiverSelections({
                organizationId,
                outreachId,
                unitType,
                unitId,
                items: writes.map((write) => ({
                    caregiver_id: write.caregiverId,
                    action: write.action,
                })),
            })
                .then(
                    (response) => {
                        const failedIds = new Set(
                            response.items
                                .filter((item) => item.error_code !== null)
                                .map((item) => item.caregiver_id),
                        );
                        return writes.filter((write) =>
                            failedIds.has(write.caregiverId),
                        );
                    },
                    // A transport failure leaves every id unresolved.
                    () => writes,
                )
                .then((failed) => {
                    if (failed.length > 0) {
                        setStagedOverrides((previousOverrides) => {
                            const nextOverrides = new Map(previousOverrides);
                            for (const write of failed) {
                                nextOverrides.delete(write.caregiverId);
                                if (write.staged) {
                                    locallyStagedIdsRef.current.delete(
                                        write.caregiverId,
                                    );
                                } else if (write.wasLocallyStaged) {
                                    // A failed withdraw of a never-persisted
                                    // local stage keeps its coordinator
                                    // ownership; else a retry would dismiss.
                                    locallyStagedIdsRef.current.add(
                                        write.caregiverId,
                                    );
                                }
                            }
                            return nextOverrides;
                        });
                        toast.error(
                            'Could not save caregiver selection changes.',
                            {
                                persistNotification: false,
                            },
                        );
                    }
                    const failedIds = new Set(
                        failed.map((write) => write.caregiverId),
                    );
                    const dismissedIds = writes
                        .filter(
                            (write) =>
                                write.action === 'dismiss' &&
                                !failedIds.has(write.caregiverId),
                        )
                        .map((write) => write.caregiverId);
                    if (dismissedIds.length > 0) {
                        onDismissalsPersisted?.(dismissedIds);
                    }
                    void mutate();
                });
        },
        [
            mutate,
            onDismissalsPersisted,
            organizationId,
            outreachId,
            unitId,
            unitType,
            userStagedCaregiverIds,
        ],
    );

    // A fresh object here would change identity on every render of every
    // table, rebuilding the memoized columns and remounting each header —
    // the WEB-CS update-depth cascade. Every member below is already
    // memoized or a primitive, so this stays stable between real changes.
    const isReady = !enabled || data !== undefined || error !== undefined;
    return useMemo(
        () => ({
            isReady,
            stagedCaregiverIds,
            selectionCoverageByCaregiverId,
            persistSelectionDelta,
        }),
        [
            isReady,
            persistSelectionDelta,
            selectionCoverageByCaregiverId,
            stagedCaregiverIds,
        ],
    );
}
